// TROIA-V3-FRONTEND/src/shared/components/ui/ranking/RankingList.tsx
import React from 'react';
import type { GamificationRankingScope, GamificationVisibility, RankingEntry } from '@troia-v3/types';

import { Avatar, AvatarFallback, AvatarImage } from '@/shared/components/ui/avatar';
import { Icon } from '@/shared/icons';

import { formatPts, initials } from './Podium';

import './ranking.css';

export interface RankingListProps {
  entries: RankingEntry[];
  scope: GamificationRankingScope;
  visibility: GamificationVisibility;
  total: number;
  meId?: string;
  /** Minha linha (vem de `RankingResponse.me`) — renderizada fora das entradas em `top3_and_me`. */
  me?: RankingEntry & { gapToNext?: number };
  /** Texto abaixo da lista (ex.: "Faltam 21 pts para o #3 — uma venda fechada resolve."). */
  gapText?: React.ReactNode;
  testIdPrefix?: string;
}

const POS_CLASS = ['gm-rk-pos--gold', 'gm-rk-pos--silver', 'gm-rk-pos--bronze'];

const TrendCell: React.FC<{ trend: RankingEntry['trend'] }> = ({ trend }) => (
  <span className={`gm-rk-num gm-rk-col--md ${trend === 'up' ? 'gm-rk-trend--up' : trend === 'down' ? 'gm-rk-trend--down' : ''}`}>
    {trend === 'up' ? '↑ 1' : trend === 'down' ? '↓ 1' : '='}
  </span>
);

const Row: React.FC<{ entry: RankingEntry; scope: GamificationRankingScope; me: boolean; testId: string }> = ({ entry, scope, me, testId }) => {
  const entity = scope === 'teams' || scope === 'units';
  return (
    <div className={`gm-rk-row ${me ? 'gm-rk-row--me' : ''}`} data-testid={testId}>
      <span className={`gm-rk-pos ${POS_CLASS[entry.position - 1] ?? ''}`}>{entry.position}</span>
      {/* Equipe/unidade também usa o Avatar shared (fallback slate do DS) — ícone no lugar das iniciais */}
      <Avatar className="gm-rk-av h-7 w-7 text-[11px]">
        {!entity && entry.avatar && <AvatarImage src={entry.avatar} alt={entry.name} />}
        <AvatarFallback className="text-[11px]">
          {entity ? <Icon name={scope === 'teams' ? 'users' : 'map-pin'} size={14} /> : initials(entry.name)}
        </AvatarFallback>
      </Avatar>
      <span className="gm-rk-n">{entry.name}{me && <span className="gm-rk-you">você</span>}</span>
      <span className="gm-rk-pts">{formatPts(entry.points)} pts</span>
      <span className="gm-rk-num gm-rk-col--md">{entity ? (entry.members ?? 0) : entry.leads}</span>
      <span className="gm-rk-num gm-rk-col--md">{entity ? '—' : entry.forms}</span>
      {entity ? <span className="gm-rk-num gm-rk-col--md">—</span> : <TrendCell trend={entry.trend} />}
    </div>
  );
};

/** Tabela do ranking (mockup 09 `.rk`): cabeçalho + linhas; em `top3_and_me` mostra o separador e a minha linha. */
export const RankingList: React.FC<RankingListProps> = ({ entries, scope, visibility, total, meId, me, gapText, testIdPrefix = 'ranking' }) => {
  const entity = scope === 'teams' || scope === 'units';
  const showMeRow = visibility === 'top3_and_me' && me !== undefined && me.position > 3;
  const between = me ? me.position - 4 : 0;
  return (
    <div className="gm-rk-wrap">
      <div className="gm-rk" data-testid={`${testIdPrefix}-list`}>
        <div className="gm-rk-row gm-rk-row--hd">
          <span>#</span><span />
          <span>{scope === 'units' ? 'Unidade' : scope === 'teams' ? 'Equipe' : 'Vendedor'}</span>
          <span style={{ textAlign: 'right' }}>Pontos</span>
          <span className="gm-rk-col--md" style={{ textAlign: 'right' }}>{entity ? 'Vendedores' : 'Leads'}</span>
          <span className="gm-rk-col--md" style={{ textAlign: 'right' }}>Formulários</span>
          <span className="gm-rk-col--md" style={{ textAlign: 'right' }}>Tendência</span>
        </div>
        {entries.map((e) => <Row key={e.id} entry={e} scope={scope} me={!entity && e.id === meId} testId={`${testIdPrefix}-row-${e.id}`} />)}
        {showMeRow && me && (
          <>
            {between > 0 && <div className="gm-rk-sep" data-testid={`${testIdPrefix}-sep`}>{between} {between === 1 ? 'vendedor' : 'vendedores'} entre você e o pódio</div>}
            <Row entry={me} scope={scope} me testId={`${testIdPrefix}-me-row`} />
          </>
        )}
        {gapText && <div className="gm-rk-gap" data-testid={`${testIdPrefix}-gap`}>{gapText}</div>}
        {entries.length === 0 && total === 0 && (
          <div className="px-4 py-10 text-center text-[13px]" style={{ color: 'var(--theme-muted-text)' }} data-testid={`${testIdPrefix}-empty`}>
            Ninguém pontuou neste período ainda.
          </div>
        )}
      </div>
    </div>
  );
};
RankingList.displayName = 'RankingList';
